import {Router} from '@angular/router';
import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subject, takeUntil} from 'rxjs';
import {DateHelper} from '../../class/helper/date.helper.class';
import {Logger} from '../../class/helper/logger.class';
import {PatientMeasurementSystemRequestDTO} from '../../dtos/measurement';
import {es_MX} from '../../locale/es_MX';
import {Loader} from '../../models/loader/loader.redux';
import {IAnalysisMeasurement, IAnalysisMeasurementResult, IResponseRequiredMeasures, MeasurementAction, MeasurementState} from '../../models/measurement';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {cloneDeep} from 'lodash';
import {IMeasurementRow} from '../new-measurement/new-measurement.component';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {RequiredMeasurementsTranslatePipe} from '../../pipes/required-measurements-translate/required-measurements-translate.pipe';
import {RedirectToEnum} from '../../enums/sphere';
import {RecordTypeEnum} from '../../enums/record';
import {VerifyRequiredMeasurementsPipe} from '../../pipes/verify-required-measurements/verify-required-measurements.pipe';
import {PatientAction} from '../../models/patient';


@Component({
  selector: 'app-basic-formulas',
  templateUrl: './basic-formulas.component.html',
  styleUrls: ['./basic-formulas.component.scss']
})
export class BasicFormulasComponent implements OnInit, OnDestroy {
  measurements$: Observable<IAnalysisMeasurement> = this.store.select<IAnalysisMeasurement>(MeasurementState.getAnalysisMeasurement);
  @Input() expertId: string;
  @Input() clinicId: string;
  @Input() patientId: string;
  @Input() serviceId: string;
  @Input() appointmentId: string;
  @Input() showCard: boolean;
  @Input() measurementsTemporal: IMeasurementRow[];
  basicCategories: { name: string; data: IAnalysisMeasurementResult[] }[] = [];
  colorsCategory: string[] = [];
  private destroy$ = new Subject();
  es_MX = es_MX;
  measurementsAvailable: IAnalysisMeasurementResult[] = [];
  // Variable usada para guardar los cambios del paso de fórmulas al menos una vez (usada en los pasos de asesoría)
  basicFormulasSaved = false;

  formatterPercent = (value) => (value === null || value === '') ? 0.01 : `${value.toFixed(2)}`;
  parserPercent = (value: string): string => value.replace('', '');
  constructor(
    private store: Store,
    private messageAlertService: MessageAlertService,
    private utilitiesService: UtilitiesService,
    private router: Router,
    private requiredMeasurementsTranslate: RequiredMeasurementsTranslatePipe,
    private verifyRequiredMeasurementsPipe: VerifyRequiredMeasurementsPipe
  ) { }

  async ngOnInit(): Promise<void> {
    this.initValues();
    this.listeners();
  }

  ngOnDestroy(): void {
    this.destroy$.next('');
    this.destroy$.complete();
  }

  listeners(): void {
    this.measurements$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async measurements => {
      if (measurements === void 0) return;
      this.measurementsAvailable = cloneDeep(measurements.basic);
      await this.getBasicCategories();
    });
  }

  initValues(): void {
    this.basicCategories = [];
    this.colorsCategory = ['magenta', 'orange', 'red', 'gold', 'volcano', 'green', 'cyan', 'blue', 'geekblue', 'purple'];
    this.measurementsAvailable = [];
    this.measurementsTemporal = [];
  }

  async getBasicCategories(): Promise<void> {
    this.basicCategories = Object.values(
      this.measurementsAvailable.reduce((groups, item) => {
        let group = [];
        if (groups[item.category]) {
          group = groups[item.category]?.data;
          if (item.hasCompleteMeasures) item.checked = true;
        }
        item.hasCompleteMeasures = this.verifyRequiredMeasurementsPipe.transform(item.requiredMeasures);
        group.push(item);
        groups[item.category] = {
          name: item.category,
          data: group,
        };
        return groups;
      }, {})
    );
    this.basicCategories.forEach((category: {name: string, data: IAnalysisMeasurementResult[]}): void => {
      const hasFormulaCompleted: IAnalysisMeasurementResult = category.data.find((formula: IAnalysisMeasurementResult) => formula.hasCompleteMeasures);
      if (hasFormulaCompleted) {
        hasFormulaCompleted.checked = true;
        return;
      }
    });
  }

  onCheckFormula(measurement: IAnalysisMeasurementResult): void {
    this.measurementsAvailable
      .filter(({category, name}: IAnalysisMeasurementResult) => category === measurement.category && name !== measurement.name)
      .forEach((item: IAnalysisMeasurementResult) => item.checked = false);
  }

  async saveBasicFormulas(): Promise<void> {
    if (
      this.measurementsAvailable.filter((item) => item.checked).length > 0
    ) {
      try {
        await firstValueFrom(this.store
          .dispatch(
            new Loader(
              true,
              'Guardando mediciones autogeneradas ...'
            )
          )
        );

        const dateHelper = new DateHelper();
        const results = this.measurementsAvailable
          .filter((measurement) => measurement.checked)
          .map((item) => {
            return {
              name: item.name,
              category: item.category,
              subcategory: item.subcategory,
              value: item.value || 0.01,
            };
          });
        const data: PatientMeasurementSystemRequestDTO = {
          dateMetadata: dateHelper.convertISOStringToDateMetadata(new Date()),
          appointmentId: this.appointmentId,
          analysisResults: results,
        };
        await firstValueFrom(this.store
          .dispatch(
            new MeasurementAction.PostAvailableFormulas(
              this.serviceId,
              this.patientId,
              this.expertId,
              data
            )
          )
        );
        await firstValueFrom(this.store
          .dispatch(
            new PatientAction.GetInformation(
              this.clinicId,
              this.patientId,
              this.serviceId
            )
          )
        );
        await firstValueFrom(this.store.dispatch(new MeasurementAction.GetAvailableFormulas(
          this.serviceId,
          this.patientId,
          this.expertId
        )));
        this.messageAlertService.createMessage(
          'success',
          'Medidas registradas con éxito.'
        );
      } catch (e) {
        Logger.log('Ocurrio un error', e?.message);
      } finally {
        this.basicFormulasSaved = true;
        await firstValueFrom(this.store.dispatch(new Loader(false)));
      }
    } else {
      this.messageAlertService.createMessage(
        'warning',
        'Debes seleccionar las mediciones que quieras registrar.',
        5000
      );
    }
  }

  /**
   * Determina hacia donde redirigir cuando falta alguna medición
   * @param item item
   * @param measurement measurement
   */
  async onRedirectTo(item: any, measurement: any): Promise<void> {
    const data: IResponseRequiredMeasures = this.requiredMeasurementsTranslate.transform(item, measurement.rules);
    let baseUrl = `/clinic/${this.clinicId}/patients/${this.patientId}/`;
    let queries = {};
    switch (data.route) {
      case RedirectToEnum.INFO:
        baseUrl += `info`;
        break;
      case RedirectToEnum.MEASUREMENTS:
        baseUrl += `measurements`;
        break;
      case RedirectToEnum.PHYSICAL_ACTIVITY:
        baseUrl += `history-data`;
        queries = {'active-record': RecordTypeEnum.PHYSICAL_ACTIVITY};
        break;
    }
    await this.router.navigate([baseUrl], {queryParams: {expert: this.expertId, ...queries}});
  }
}
